import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    padding: '72px 0',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  marginBottom: '12px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  marginBottom: '56px',
  maxWidth: '720px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '36px',
  },
  '& span': { color: theme.palette.text.secondary },
}));

const Group = styled(Box)(({ theme }) => ({
  padding: '28px',
  borderRadius: '14px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  height: '100%',
  animation: `${fadeIn} 0.4s ease-out`,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
}));

const GroupLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: '20px',
}));

const SkillList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const SkillItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '12px',
  fontSize: '0.95rem',
  '& .name': {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  '& .dim': {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  '& .dot': {
    width: 4,
    height: 4,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    marginRight: '4px',
    flexShrink: 0,
  },
}));

const Skills = () => {
  const groups = [
    {
      label: '// Languages',
      items: [
        { name: 'TypeScript', note: 'daily driver' },
        { name: 'JavaScript', note: '' },
        { name: 'Ruby', note: '' },
        { name: 'Python', note: 'research' },
        { name: 'Java', note: '' },
        { name: 'SQL', note: '' },
      ],
    },
    {
      label: '// Frontend',
      items: [
        { name: 'React', note: '' },
        { name: 'Material UI', note: '' },
        { name: 'Tailwind CSS', note: '' },
        { name: 'HTML / CSS', note: '' },
      ],
    },
    {
      label: '// Backend & data',
      items: [
        { name: 'Ruby on Rails', note: '' },
        { name: 'Node.js', note: '' },
        { name: 'FastAPI', note: '' },
        { name: 'REST APIs', note: '' },
        { name: 'PostgreSQL', note: '' },
      ],
    },
    {
      label: '// Tools & practice',
      items: [
        { name: 'Git', note: '' },
        { name: 'Docker', note: '' },
        { name: 'Testing', note: 'JUnit, Jest, RSpec' },
        { name: 'Debugging', note: '' },
        { name: 'PowerBI', note: 'reporting' },
        { name: 'Linux CLI', note: '' },
      ],
    },
  ];

  return (
    <Section id="skills">
      <Container maxWidth="lg">
        <Eyebrow>Toolkit</Eyebrow>
        <SectionHeading>
          What I work with.{' '}
          <span>
            Tools I use day-to-day. I add or drop things based on the problem,
            not the trend cycle.
          </span>
        </SectionHeading>

        <Grid container spacing={3}>
          {groups.map((g) => (
            <Grid item xs={12} sm={6} md={3} key={g.label}>
              <Group>
                <GroupLabel>{g.label}</GroupLabel>
                <SkillList>
                  {g.items.map((s) => (
                    <SkillItem key={s.name}>
                      <span className="dot" />
                      <span className="name">{s.name}</span>
                      {s.note && <span className="dim">· {s.note}</span>}
                    </SkillItem>
                  ))}
                </SkillList>
              </Group>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Skills;
